import { useCallback, useEffect, useState } from "react";
import { adminListSubmissions, type FormSubmission } from "@/lib/admin-api";

/**
 * 폼 접수함 — kangstudy_form_submissions 조회 (Edge Function 경유, 관리자 전용).
 *
 * 상담신청·교육신청·리포트·뉴스레터·강의문의 5개 공개 폼이 여기로 쌓인다
 * (2026-08-18 readdy form API 에서 이관). 테이블은 RLS 정책 0개라 이 화면이
 * 유일한 열람 경로다(그 외에는 Supabase 대시보드뿐).
 */
const FORM_TYPE_META: Record<string, { label: string; color: string }> = {
  consultation: { label: "상담신청", color: "bg-amber-100 text-amber-800" },
  course_registration: { label: "교육신청", color: "bg-emerald-100 text-emerald-700" },
  report_download: { label: "리포트", color: "bg-sky-100 text-sky-700" },
  newsletter: { label: "뉴스레터", color: "bg-purple-100 text-purple-700" },
  lecture_inquiry: { label: "강의문의", color: "bg-rose-100 text-rose-700" },
};

/** payload 에서 표에 이미 보이는 키·허니팟은 펼침 화면에서 숨긴다. */
const HIDDEN_PAYLOAD_KEYS = new Set(["name", "phone", "email", "phone_alt", "company_alt"]);

const PAGE_SIZE = 30;

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function SubmissionsInbox() {
  const [rows, setRows] = useState<FormSubmission[]>([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminListSubmissions({
        formType: filter === "all" ? undefined : filter,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setRows(res.rows);
      setTotal(res.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "접수함을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      {/* 상단 바 */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-[#1C1917]">폼 접수함</h3>
          <span className="text-xs text-[#8C8780]">총 {total.toLocaleString()}건</span>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1 px-3 py-1.5 text-xs border border-[#E8E5E0] rounded-lg text-[#5C5650] hover:border-[#C4956A] hover:text-[#C4956A] transition-colors whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i> 새로고침
        </button>
      </div>

      {/* 유형 필터 */}
      <div className="flex flex-wrap items-center gap-1.5">
        {[["all", "전체"], ...Object.entries(FORM_TYPE_META).map(([k, v]) => [k, v.label])].map(
          ([key, label]) => (
            <button
              key={key}
              onClick={() => {
                setFilter(key);
                setPage(0);
                setExpandedId(null);
              }}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors whitespace-nowrap ${
                filter === key
                  ? "bg-[#1C1917] text-white border-[#1C1917]"
                  : "bg-white text-[#5C5650] border-[#E8E5E0] hover:border-[#C4956A]"
              }`}
            >
              {label}
            </button>
          ),
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
          {error}
        </div>
      )}

      {loading && (
        <div className="py-16 text-center">
          <div className="w-7 h-7 mx-auto border-2 border-[#E8E5E0] border-t-[#C4956A] rounded-full animate-spin" />
        </div>
      )}

      {!loading && rows.length === 0 && !error && (
        <div className="py-16 text-center text-sm text-[#8C8780]">
          <i className="ri-inbox-line text-3xl text-[#D1CCC2] block mb-2"></i>
          접수된 신청이 없습니다
        </div>
      )}

      {/* 목록 */}
      {!loading && rows.length > 0 && (
        <div className="border border-[#E8E5E0] rounded-xl overflow-hidden divide-y divide-[#F0EDE8]">
          {rows.map((r) => {
            const meta = FORM_TYPE_META[r.form_type] ?? {
              label: r.form_type,
              color: "bg-gray-100 text-gray-600",
            };
            const detail = Object.entries(r.payload).filter(
              ([k, v]) => !HIDDEN_PAYLOAD_KEYS.has(k) && String(v).trim() !== "",
            );
            const expanded = expandedId === r.id;
            return (
              <div key={r.id} className="bg-white">
                <button
                  onClick={() => setExpandedId(expanded ? null : r.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#FAFAF8] transition-colors"
                >
                  <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full whitespace-nowrap ${meta.color}`}>
                    {meta.label}
                  </span>
                  <span className="text-xs font-medium text-[#1C1917] whitespace-nowrap">
                    {r.name || "-"}
                  </span>
                  <span className="text-xs text-[#8C8780] whitespace-nowrap hidden sm:inline">
                    {r.phone || r.email || ""}
                  </span>
                  <span className="ml-auto text-[11px] text-[#B8B3AE] whitespace-nowrap">
                    {formatDate(r.created_at)}
                  </span>
                  <i className={`ri-arrow-${expanded ? "up" : "down"}-s-line text-[#B8B3AE]`}></i>
                </button>
                {expanded && (
                  <div className="px-4 pb-4 pt-1 bg-[#FAFAF8] border-t border-[#F0EDE8]">
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                      {r.email && (
                        <div className="flex gap-2">
                          <dt className="text-[#8C8780] whitespace-nowrap">이메일</dt>
                          <dd className="text-[#1C1917] break-all">{r.email}</dd>
                        </div>
                      )}
                      {r.phone && (
                        <div className="flex gap-2">
                          <dt className="text-[#8C8780] whitespace-nowrap">연락처</dt>
                          <dd className="text-[#1C1917]">{r.phone}</dd>
                        </div>
                      )}
                      {detail.map(([k, v]) => (
                        <div key={k} className="flex gap-2">
                          <dt className="text-[#8C8780] whitespace-nowrap">{k}</dt>
                          <dd className="text-[#1C1917] break-all">{String(v)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 페이지네이션 */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 text-xs">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 border border-[#E8E5E0] rounded-lg disabled:opacity-40 hover:border-[#C4956A] transition-colors"
          >
            이전
          </button>
          <span className="text-[#8C8780]">
            {page + 1} / {totalPages}
          </span>
          <button
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 border border-[#E8E5E0] rounded-lg disabled:opacity-40 hover:border-[#C4956A] transition-colors"
          >
            다음
          </button>
        </div>
      )}
    </div>
  );
}
